<template>
    <div class="page-container">
        <div class="settings-form">
            <h2 class="page-title">媒体模型记录管理</h2>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.medium_name" placeholder="请输入媒体接口名称" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    新增媒体配置
                </el-button>
            </div>

            <!-- 接口列表 -->
            <el-table :data="mediumList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
                <el-table-column prop="medium_name" label="接口名称" width="180" />
                <el-table-column prop="medium_api" label="接口类型" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.medium_api === 0" type="success">免费接口</el-tag>
                        <el-tag v-else-if="row.medium_api === 1" type="success">JZD接口</el-tag>
                        <el-tag v-else type="info">其他接口</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="medium_type" label="媒体类型" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.medium_type === 1" type="warning">图片</el-tag>
                        <el-tag v-else-if="row.medium_type === 2" type="primary">视频</el-tag>
                        <el-tag v-else type="info">音乐</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="key_preview" label="密钥预览" width="130">
                    <template #default="{ row }">
                        <span class="key-preview">{{ row.key_preview }}******</span>
                    </template>
                </el-table-column>
                <el-table-column prop="medium_url" label="接口地址" min-width="200" show-overflow-tooltip />
                <el-table-column prop="notes" label="备注" width="150" />
                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="200" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchMediumList"
                    @current-change="fetchMediumList" />
            </div>
        </div>

        <!-- 新增/编辑对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="700px" :close-on-click-modal="false">
            <el-form ref="mediumForm" :model="mediumForm" label-width="120px" :rules="formRules">
                <el-form-item label="接口类型" prop="medium_api">
                    <el-select v-model="mediumForm.medium_api" placeholder="选择接口类型" size="large" style="width: 100%"
                        @change="handleApiTypeChange">
                        <el-option label="免费接口" :value="0" />
                        <el-option label="JZD接口" :value="1" />
                        <el-option label="其他接口" :value="2" />
                    </el-select>
                </el-form-item>

                <el-form-item label="媒体类型" prop="medium_type">
                    <el-select v-model="mediumForm.medium_type" placeholder="选择媒体类型" size="large" style="width: 100%"
                        @change="handleMediaTypeChange">
                        <el-option label="图片" :value="1" />
                        <el-option label="视频" :value="2" />
                        <el-option label="音乐" :value="3" />
                    </el-select>
                </el-form-item>

                <el-form-item label="接口名称" prop="medium_name">
                    <el-input v-model="mediumForm.medium_name" placeholder="请输入接口名称" size="large" />
                </el-form-item>

                <el-form-item label="模型名称" prop="medium_model" v-if="mediumForm.medium_api === 1">
                    <el-input v-model="mediumForm.medium_model" placeholder="请输入模型名称，如 grok-4-image" size="large" />
                </el-form-item>

                <el-form-item label="接口地址" prop="medium_url" style="width: 100%">
                    <div class="api-url-container" style="width: 100%;">
                        <el-select v-model="urlPrefix" style="width: 120px; margin-right: 10px">
                            <el-option label="https://" value="https://" />
                            <el-option label="http://" value="http://" />
                        </el-select>
                        <el-input v-model="mediumForm.medium_url" placeholder="请输入媒体接口地址" size="large"
                            style="flex: 1" />
                        <el-tooltip content="查看文档" placement="top">
                            <el-link :href="apiDocs[mediumForm.medium_api]?.[mediumForm.medium_type]" target="_blank"
                                style="margin-left: 10px"
                                v-if="apiDocs[mediumForm.medium_api]?.[mediumForm.medium_type]">
                                <el-icon>
                                    <Document />
                                </el-icon>
                            </el-link>
                        </el-tooltip>
                    </div>
                </el-form-item>

                <el-form-item label="接口密钥" prop="medium_key">
                    <el-input v-model="mediumForm.medium_key" placeholder="请输入媒体接口密钥" size="large" show-password />
                </el-form-item>

                <el-form-item label="备注信息">
                    <el-input v-model="mediumForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitMediumForm">确认</el-button>

            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Plus, Edit, Delete, Document
} from '@element-plus/icons-vue'

export default {
    name: 'SystemMediumApi',
    components: {
        Search, Plus, Edit, Delete, Document
    },
    data() {
        // URL验证函数
        const validateUrl = (rule, value, callback) => {
            if (!value) {
                callback(new Error('请输入接口地址'));
            } else if (!/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i.test(value)) {
                callback(new Error('请输入有效的URL地址'));
            } else {
                callback();
            }
        };

        return {
            // 不同接口的文档链接映射
            apiDocs: {
                1: {
                    1: "https://api.juzhendi.com/",
                    2: "https://api.juzhendi.com/",
                    3: "https://api.juzhendi.com/"
                }
            },
            urlPrefix: 'https://',
            loading: false,
            dialogVisible: false,
            dialogTitle: '新增媒体配置',
            isEditMode: false,
            searchForm: {
                medium_name: ''
            },
            mediumForm: {
                id: null,
                medium_api: null,
                medium_type: null,
                medium_name: '',
                medium_model: '',
                medium_url: '',
                medium_key: '',
                notes: ''
            },
            formRules: {
                medium_api: [
                    { required: true, message: '请选择接口类型', trigger: 'blur' }
                ],
                medium_type: [
                    { required: true, message: '请选择媒体类型', trigger: 'blur' }
                ],
                medium_name: [
                    { required极速模式: true, message: '请输入接口名称', trigger: 'blur' }
                ],
                medium_url: [
                    { required: true, validator: validateUrl, trigger: 'blur' }
                ],
                medium_key: [
                    { required: true, message: '请输入接口密钥', trigger: 'blur' }
                ]
            },
            mediumList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    watch: {
        'mediumForm.medium_url': function (newVal) {
            if (newVal) {
                // 自动添加协议前缀如果缺失
                if (!/^https?:\/\//.test(newVal) && this.urlPrefix) {
                    this.mediumForm.medium_url = newVal.replace(/^(https?:\/\/)?/, '');
                }
            }
        }
    },
    mounted() {
        this.fetchMediumList();
    },
    methods: {
        // 处理媒体类型变更
        handleMediaTypeChange(value) {
            if (this.mediumForm.medium_api === 0) { // 免费接口
                // 根据媒体类型更新接口名称
                switch (value) {
                    case 1: // 图片
                        this.mediumForm.medium_name = "免费图片接口";
                        break;
                    case 2: // 视频
                        this.mediumForm.medium_name = "免费视频接口";
                        break;
                    case 3: // 音乐
                        this.mediumForm.medium_name = "免费音乐接口";
                        break;
                }
            } else if (this.mediumForm.medium_api === 1) {
                // JZD接口类型 - 自动填充预设URL和模型
                switch (value) {
                    case 1: // 图片
                        this.mediumForm.medium_url = "api.juzhendi.com/api/v1";
                        this.mediumForm.medium_model = this.mediumForm.medium_model || "grok-4-image";
                        break;
                    case 2: // 视频
                        this.mediumForm.medium_url = "api.juzhendi.com/api/v1";
                        this.mediumForm.medium_model = this.mediumForm.medium_model || "";
                        break;
                    case 3: // 音乐
                        this.mediumForm.medium_url = "api.juzhendi.com/api/v1";
                        this.mediumForm.medium_model = this.mediumForm.medium_model || "";
                        break;
                }
            }
        },

        // 处理接口类型变更
        handleApiTypeChange(value) {
            if (value === 0) { // 免费接口
                // 设置默认值
                this.mediumForm.medium_name = "免费图片接口";
                this.mediumForm.medium_url = this.getDomainWithoutProtocol(window.location.origin);

                this.mediumForm.medium_key = "123456";
            } else if (value === 1 && this.mediumForm.medium_type) {
                // 当切换为JZD接口时，如果已经选择了媒体类型，则自动填充
                this.handleMediaTypeChange(this.mediumForm.medium_type);
            } else if (value !== 1) {
                // 其他接口清空预设
                this.mediumForm.medium_url = '';
                this.mediumForm.medium_key = '';
                this.mediumForm.medium_name = '';
            }
        },

        // 获取媒体配置列表
        async fetchMediumList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.medium_name ? 'search' : 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                // 如果有搜索条件，加入搜索参数
                if (this.searchForm.medium_name) {
                    params.medium_name = this.searchForm.medium_name;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/medium_model_key.php`,
                    params
                );

                if (data.data) {
                    this.mediumList = data.data;
                    this.pagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取媒体配置失败');
                console.error('获取媒体配置错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchMediumList();
        },

        getDomainWithoutProtocol(url) {
            return url.replace(/^https?:\/\//, '');
        },
        // 打开新增对话框
        openAddDialog() {
            this.dialogTitle = '新增媒体配置';
            this.isEditMode = false;
            this.mediumForm = {
                id: null,
                medium_api: 0, // 默认选择免费接口
                medium_type: 1, // 默认选择图片
                medium_name: '免费图片接口',
                medium_model: '',
                medium_url: this.getDomainWithoutProtocol(window.location.origin), // 去掉协议头
                medium_key: '123456',
                notes: ''
            };
            this.urlPrefix = 'https://';
            this.dialogVisible = true;
        },

        // 打开编辑对话框
        openEditDialog(row) {
            this.dialogTitle = '编辑媒体配置';
            this.isEditMode = true;
            this.mediumForm = {
                id: row.id,
                medium_api: row.medium_api,
                medium_type: row.medium_type,
                medium_name: row.medium_name,
                medium_model: row.medium_model || '',
                medium_url: row.medium_url,
                medium_key: row.medium_key, // 实际应用中需要从后端获取完整密钥
                notes: row.notes
            };

            // 设置URL协议前缀
            if (row.medium_url && /^https?:\/\//.test(row.medium_url)) {
                this.urlPrefix = row.medium_url.match(/^https?:\/\//)[0];
                this.mediumForm.medium_url = row.medium_url.replace(/^https?:\/\//, '');
            } else {
                this.urlPrefix = 'https://';
            }

            this.dialogVisible = true;
        },

        // 提交表单（新增/编辑）
        submitMediumForm() {
            this.$refs.mediumForm.validate(async (valid) => {
                if (valid) {
                    try {
                        // 处理完整URL
                        const fullUrl = this.urlPrefix + this.mediumForm.medium_url;

                        const action = this.isEditMode ? 'update' : 'add';
                        const payload = {
                            ...this.mediumForm,
                            medium_url: fullUrl,
                            store_id: this.userInfo.id
                        };

                        const { data } = await axios.post(
                            `${config.BASE_URL}/shop/api/medium_model_key.php`,
                            {
                                action,
                                ...payload
                            }
                        );

                        if (data.success) {
                            this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
                            this.dialogVisible = false;
                            this.fetchMediumList();
                        } else {
                            this.$message.error(data.error || '操作失败');
                        }
                    } catch (error) {
                        const errorMsg = error.response?.data?.error || error.message || '操作失败';
                        this.$message.error(`操作失败: ${errorMsg}`);
                        console.error('表单提交错误:', error);
                    }
                }
            });
        },

        // 删除配置
        async handleDelete(id) {
            this.$confirm('确定要删除该媒体配置吗？此操作不可恢复', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning',
                icon: 'el-icon-warning',
                customClass: 'delete-confirm-dialog'
            }).then(async () => {
                try {
                    const { data } = await axios.post(
                        `${config.BASE_URL}/shop/api/medium_model_key.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (data.success) {
                        this.$message.success('删除成功');
                        this.fetchMediumList();
                    } else {
                        this.$message.error(data.error || '删除失败');
                    }
                } catch (error) {
                    const errorMsg = error.response?.data?.error || error.message || '删除失败';
                    this.$message.error(`删除失败: ${errorMsg}`);
                    console.error('删除错误:', error);
                }
            }).catch(() => { });
        }
    }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;

/* 对话框样式优化 */
:deep(.el-dialog) {
    border-radius: 12px;

    .el-dialog__header {
        padding: 20px 24px;
        border-bottom: 1px solid #ebeef5;
        margin-right: 0;

        .el-dialog__title {
            font-weight: 600;
            color: #1f2937;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .el-dialog__footer {
        padding: 16px 24px;
        border-top: 1px solid #ebeef5;
    }

    .el-form-item__content {
        width: 100%;
    }

    .el-input,
    .el-select {
        width: 100%;
    }

    .el-form-item {
        margin-bottom: 22px;
    }
}

.dialog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;

    .actions {
        display: flex;
        gap: 10px;

        .el-button {
            margin-left: 0;
        }
    }
}

/* 新增特定样式 */
.key-preview {
    font-family: monospace;
    background-color: #f5f7fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
}

/* 修改后的API URL容器样式 */
.api-url-container {
    display: flex;
    align-items: center;
    width: 100%;
}
</style>